import React, { ReactElement } from 'react';
import styled from 'styled-components';

import { createMarkup, stripTags } from 'app/utils';
// import { useModules } from 'features/modules';
import { ContentQuizQuestionsState, useContents } from 'features/contents';
// import { maskTeachers } from 'features/teachers';

import { Flex, Typography, Button } from 'components';

const Bullet = styled.div`
  width: 26px;
  height: 26px;
  position: absolute;
  left: 18px;
  border-radius: 50%;
  cursor: ${(props: any) => (props['data-link'] ? 'pointer' : 'inherit')};

  &::before {
    content: '';
    display: block;
    width: 14px;
    height: 14px;

    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  ${(props: any) => {
    return `
      top: 19px;

      ${props.theme.mediaQueries.desktop} {
        top: 8px;
      }
    `;
  }}
`;

const Square = styled.div`
  width: 18px;
  height: 18px;
  position: absolute;
  left: 22px;
  background: ${({ theme }) => theme.colors.primary_400};

  ${(props: any) => {
    return `
      top: 20px;

      ${props.theme.mediaQueries.desktop} {
        top: 32px;
      }
    `;
  }}
`;

const InteractionItem = styled.div`
  position: relative;

  ${Bullet} {
    background: ${(props: any) => (props['data-active'] ? props.theme.colors.quizSidebarBg : props.theme.colors.quizSidebarBg)};

    &::before {
      border: 2px solid
        ${(props: any) =>
          props['data-active']
            ? props.theme.colors.primary_400
            : props['data-right'] === null
            ? props.theme.colors.gray_600
            : props['data-right']
            ? props.theme.colors.primary_400
            : props.theme.colors.alert_400};
      background: ${(props: any) =>
        props['data-active']
          ? props.theme.colors.primary_400
          : props['data-resolved']
          ? props['data-right'] === null
            ? props.theme.colors.gray_600
            : props['data-right']
            ? props.theme.colors.primary_400
            : props.theme.colors.alert_400
          : props.theme.colors.quizSidebarBg};
    }
  }

  &::before {
    content: '';
    display: block;
    width: 2px;
    background: ${({ theme }) => theme.colors.gray_600};
    position: absolute;
    top: 0;
    left: 30px;
  }

  ${(props: any) => {
    return `
      padding: 20px 20px ${props['data-first'] ? '50px' : '20px'} 60px;

      &::before {
        height: ${props['data-first'] ? 'calc(100% - 30px)' : props['data-last'] ? '30px' : '100%'};
        margin-top: ${props['data-first'] ? '30px' : '0'};
      }

      ${props.theme.mediaQueries.desktop} {
        padding:         ${props['data-first'] ? '30px 30px 80px 60px' : props['data-last'] ? '10px 30px 0px 60px' : '10px 30px 10px 60px'};

        &::before {
          height: ${props['data-first'] ? 'calc(100% - 55px)' : props['data-last'] ? '30px' : '100%'};
          margin-top: ${props['data-first'] ? '55px' : '0'};
        }
      }
    `;
  }}
`;

interface QuizSidebarProps {
  questions: ContentQuizQuestionsState;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<string>>;
  questionsCompleted: boolean;
  sendQuestions: () => void;
  questionsSended: boolean;
}

const QuizSidebar = ({ questions, setCurrentQuestion, questionsCompleted, sendQuestions, questionsSended }: QuizSidebarProps): ReactElement => {
  // const { moduleDetails } = useModules();
  const { contentDetails } = useContents();

  if (!(contentDetails && contentDetails.quiz)) return <></>;

  return (
    <Flex flexDirection="column" pb={{ m: 40, d: 60 }}>
      <InteractionItem data-first="true">
        <Square />

        <Flex flexDirection="column">
          <Typography type="type3" upper color="color80" lineHeight="1.3">
            {contentDetails.productClassTimelineName || contentDetails.productName}
          </Typography>

          <Typography type="type6" fontWeight="bold" color="color100" mt={{ m: 2, d: 3 }}>
            {contentDetails.name}
          </Typography>

          {Boolean(contentDetails.teacher) && (
            <Typography type="type3" color="color80" mt={{ m: 4, d: 5 }}>
              Por: {contentDetails && contentDetails.teacher && contentDetails.teacher.name}
            </Typography>
          )}
        </Flex>
      </InteractionItem>

      {Object.keys(questions).map((questionUuid, index) => {
        const question = questions[questionUuid];

        // let answerStatus = '';
        // let answerColor = 'color100';
        // let answerPath = mdiCheckboxMarkedCircle;
        // let answerFontWeight = 'normal';

        // if (
        //   question.rightQuestionAnswerChoiceUuid &&
        //   question.rightQuestionAnswerChoiceUuid === answer.answerUuid &&
        //   question.answerUuid === answer.answerUuid
        // ) {
        //   answerStatus = 'right-right';
        //   answerColor = 'primary_400';
        //   answerPath = mdiCheckboxMarkedCircle;
        //   answerFontWeight = 'bold';
        // }

        return (
          <InteractionItem
            key={`interaction-${index.toString()}`}
            data-last={index + 1 === contentDetails.quiz?.questions.length}
            data-active={!question.rightQuestionAnswerChoiceUuid && question.currentQuestion}
            data-resolved={question.answerUuid}
            data-right={question.rightQuestionAnswerChoiceUuid ? question.rightQuestionAnswerChoiceUuid === question.answerUuid : null}
          >
            <Bullet data-link onClick={() => setCurrentQuestion(question.questionUuid)} />

            <Typography
              onClick={() => setCurrentQuestion(question.questionUuid)}
              type="type2"
              lineHeight="1.4"
              color="color100"
              style={{ cursor: 'pointer' }}
              fontWeight={question.currentQuestion ? 'bold' : ''}
            >
              <div
                dangerouslySetInnerHTML={createMarkup(
                  `${`${question.questionIndex}`.padStart(2, '0')} . ${stripTags(question.questionText).substr(0, 80).trim()}...`
                )}
              />
            </Typography>
          </InteractionItem>
        );
      })}
      {!questionsSended && (
        <Flex justifyContent="center" mt={7}>
          <Button
            disabled={!questionsCompleted}
            bg={questionsCompleted ? 'primary_400' : 'gray_500'}
            ml={2}
            title="Enviar atividade"
            onClick={sendQuestions}
          >
            Enviar atividade
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default QuizSidebar;
